import React, { useState } from 'react'
import { Button, Descriptions, message, Form, Row, Col } from 'antd';
import { WarningFilled } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { stepperMoving } from '../../stores/actions';
import { request } from '../../../../shared/src/apis';
import { errorMessage } from '../../utils/commonStringsConsts';
import { requestEarlySettlementApi, deleteEarlySettlementRequestApi } from '../../utils/apisConsts';
import { IRequestEarlySettlement } from "../../utils/commonInterfaces";
import { ModalComponent, ReusableDataTable } from '@operations/shared';
import helper from '../../utils/helper';

export const RequestEarlySettlement = () => {
    const settlementData = useSelector((state: any) => state?.actionsReducer?.info);
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [toggleDeleteModal, setToggleDeleteModal] = useState(false);
    const [form] = Form.useForm();

    const DeleteContent = <div className='px-3 my-3 text-center'>
        <div className='flex items-center justify-center w-12 h-12 mx-auto rounded-full' style={{ backgroundColor: "#fecaca" }}>
            <WarningFilled className='flex items-center justify-center text-2xl' style={{ color: "#c81e1f" }} />
        </div>
        <h1 className='my-4 text-xl'>Delete Request</h1>
        <p>
            Are you sure you want to delete this Request?<br />
            This action is permanent and cannot be undone.
        </p>
    </div>


    const arrayKeys = Object.keys(settlementData || {}).filter(
        key => Array.isArray(settlementData?.[key])
    );

    const handleCloseModal = () => {
        if (!loading) {
            setToggleDeleteModal(!toggleDeleteModal);
        }
    };

    const handleDateChange = async () => {
        setLoading(true);
        try {
            const res: { [key: string]: any } = await dispatch(request({
                url: `${deleteEarlySettlementRequestApi}${settlementData?.existingRequest?.id}`, method: "DELETE",
            }));
            if (res?.data?.status === 200) {
                message.success(res?.data?.message, 4);
                setLoading(false);
                setToggleDeleteModal(false);
                dispatch(stepperMoving(0));
            } else {
                message.error(res?.data?.message, 4);
                setLoading(false);
                dispatch(stepperMoving(0));
            }
        } catch (error) {
            message.error(errorMessage, 4);
            setLoading(false);
        }
    }

    const getDynamicColumns = (arr: any[]) => {
        if (!arr || !arr.length || typeof arr[0] !== 'object') return [];
        return Object.keys(arr[0])
            .filter(key => key.toLowerCase() !== 'uuid')
            .map(key => {
                const lowerKey = key.toLowerCase();
                const isDate = lowerKey.includes('date');
                const isMoney = [
                    'amount', 'revenue', 'discount', 'fees', 'principal', 'refund', 'total'
                ].some(moneyKey => lowerKey.includes(moneyKey));

                let title = helper.unCamelCase(key);
                if (key === 'nextPaymentCycle' || key === 'NextPaymentCycle' || key === 'next_payment_cycle' || key.toLowerCase() === 'nextpaymentcycle') {
                    title = 'First Discounted Payment Cycle';
                }

                return {
                    title: <span title={title}>{title}</span>,
                    dataIndex: key,
                    key: key,
                    ellipsis: {
                        showTitle: false
                    },
                    render: (value: any) => {
                        if (isDate && value) {
                            return <span style={{ display: 'inline-block', minWidth: 120 }}>{helper.changeDateFormat(value, 'YYYY/MM/DD')}</span>;
                        }
                        if (isMoney && value !== undefined && value !== null && value !== '') {
                            return <span style={{ display: 'inline-block', minWidth: 120 }}>{helper.amountFormatter(value)} SAR</span>;
                        }
                        return (
                            <span style={{ display: 'inline-block', minWidth: 120 }}>{String(value || '')}</span>
                        );
                    }
                };
            });
    };


    const handleRequestEarlySettlement = async (values: IRequestEarlySettlement) => {
        if (!settlementData) {
            message.error('Missing required data', 5);
            return;
        }
        const requestData = {
            "userID": settlementData?.userID || '',
            "startFromPaymentCycle": settlementData?.settlementPaymentCycle || '',
        }
        setLoading(true);
        try {
            const apiData: any = await dispatch(request({ method: 'POST', url: requestEarlySettlementApi, data: requestData }))
            const res = await apiData?.data;
            if (res?.status === 200) {
                message.success(res?.message || 'Request submitted successfully', 5);
                dispatch(stepperMoving(0));
                return
            }
            message.error(res?.message || 'An error occurred while submitting the request', 5);
        } catch (error) {
            message.error(errorMessage, 5);
        }
        setLoading(false);
    }

    if (!settlementData) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', marginTop: '20px' }}>

                {
                    settlementData?.existingRequest && (
                        <>
                            <h2 style={{ fontSize: '20px', fontWeight: 600, padding: '10px 0' }}>
                                Request Details
                            </h2>
                            <Descriptions bordered style={{ padding: '10px 0' }}>
                                <Descriptions.Item label="Request Creation Date" span={3} style={{ padding: '10px' }}>
                                    {helper.changeDateFormat(settlementData?.existingRequest?.createdAt)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Start Payment Cycle" span={3} style={{ padding: '10px' }}>
                                    {settlementData?.existingRequest?.startFromPaymentCycle}
                                </Descriptions.Item>
                                <Descriptions.Item label="Request Created By" span={3} style={{ padding: '10px' }}>
                                    {settlementData?.existingRequest?.createdByName || 'N/A'}
                                </Descriptions.Item>
                            </Descriptions>
                        </>
                    )
                }


                <h2 style={{ fontSize: '20px', fontWeight: 600, padding: '10px 0' }}>Early Settlement</h2>

                <Descriptions bordered style={{ padding: '10px 0' }}>
                    <Descriptions.Item label="User ID" span={3} style={{ padding: '10px' }}>
                        {settlementData?.userID || ''}
                    </Descriptions.Item>

                    <Descriptions.Item label="First Discounted Payment Cycle" span={3} style={{ padding: '10px' }}>
                        {settlementData?.nextPaymentCycle || ''}
                    </Descriptions.Item>

                    <Descriptions.Item label="Future Revenue Eligible for discount (after 2 cycles)" span={3} style={{ padding: '10px' }}>
                        {
                            `${helper.amountFormatter(settlementData?.futureRevenueEligibleForDiscount)} SAR`
                        }
                    </Descriptions.Item>

                    <Descriptions.Item label="Total Due Today" span={3} style={{ padding: '10px' }}>
                        {helper.amountFormatter(settlementData?.totalDueToday)} SAR
                    </Descriptions.Item>

                    <Descriptions.Item label="Total Due - Discount (future revenue after 2 cycles)" span={3} style={{ padding: '10px' }}>
                        {helper.amountFormatter(settlementData?.futureRevenueAfterTwoCycles)} SAR
                    </Descriptions.Item>

                    <Descriptions.Item label="Best Pay Before or On" span={3} style={{ padding: '10px' }}>
                        {settlementData?.bestPayBeforeOrOn}
                    </Descriptions.Item>

                    <Descriptions.Item label="Maximum Discount Possible (after 1 cycle)" span={3} style={{ padding: '10px' }}>
                        {helper.amountFormatter(settlementData?.maximumPossibleDiscount)} SAR
                    </Descriptions.Item>

                    <Descriptions.Item label="Best Settlement Amount" span={3} style={{ padding: '10px' }}>
                        {helper.amountFormatter(settlementData?.bestSettlementAmount)} SAR
                    </Descriptions.Item>
                </Descriptions>

                {arrayKeys?.map(key => (
                    <div key={key} style={{ padding: '10px 0' }}>
                        <h3 style={{
                            marginBottom: 8,
                            color: '#1890ff',
                            fontWeight: 600,
                            fontSize: '16px',
                            borderBottom: '2px solid #1890ff',
                            paddingBottom: '4px'
                        }}>
                            {helper.unCamelCase(key)}
                        </h3>
                        <ReusableDataTable
                            columns={getDynamicColumns(settlementData?.[key] || [])}
                            hiddenRefreshing
                            rows={settlementData?.[key] || []}
                            apiLink={``}
                            pagination={false}
                            loading={loading}
                            scrolling={{ x: 'max-content', y: 350 }}
                        />
                    </div>
                ))}
            </div>
            <div>
                <Form name="nest-messages" onFinish={handleRequestEarlySettlement} form={form}>
                    <Row className="inputsRow">
                        {/* <Col span={12}>
                            <label>Request Early Settlement Date <span>*</span></label>
                            <Form.Item
                                name="earlySettlementDate"
                                rules={[{ required: true, message: 'Please select a date' }]}
                            >
                                <DatePicker style={{ width: "100%" }} defaultValue={moment()} onChange={handleDateChange} />
                            </Form.Item>
                        </Col> */}

                        <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                            {
                                !settlementData?.existingRequest && (
                                    <Form.Item style={{ margin: 0 }}>
                                        <Button 
                                            type="primary"
                                            htmlType="submit" 
                                            loading={loading}
                                            style={{
                                                height: '40px',
                                                width: '200px',
                                                padding: '0 24px',
                                                borderRadius: '6px',
                                                fontWeight: 500,
                                                fontSize: '14px',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            Request a settlement
                                        </Button>
                                    </Form.Item>
                                )
                            }
                            {
                                settlementData?.existingRequest && (
                                    <Form.Item style={{ margin: 0 }}>
                                        <Button 
                                            danger 
                                            loading={false} 
                                            onClick={handleCloseModal}
                                            style={{
                                                height: '40px',
                                                width: '200px',
                                                padding: '0 24px',
                                                borderRadius: '6px',
                                                fontWeight: 500,
                                                fontSize: '14px',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </Form.Item>
                                )
                            }
                            <Form.Item style={{ margin: 0 }}>
                                <Button 
                                    style={{
                                        height: '40px',
                                        width: '200px',
                                        padding: '0 24px',
                                        borderRadius: '6px',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        color: '#1890ff',
                                        border: '1px solid #1890ff',
                                        backgroundColor: '#fff',
                                        boxSizing: 'border-box'
                                    }} 
                                    loading={false} 
                                    onClick={() => dispatch(stepperMoving(0))}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </div>
            <ModalComponent 
                content={DeleteContent} 
                visibleModal={toggleDeleteModal} 
                handleCloseModal={handleCloseModal} 
                handleAcceptModal={handleDateChange} 
                okText={'Delete'} 
                loadingConfirm={loading}
                cancelDisable={loading}
            />

        </>
    )
}
